import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { getUserIntegrationsStatus } from './userIntegrationService';

/**
 * Fetch Gmail messages for a user, along with the connection status.
 * Callers use the status to tell "this user has no matching emails" apart from
 * "this user's Gmail is disconnected", which otherwise both look like an empty list.
 *
 * `profileConnected` mirrors the same `integrations.gmail.connected` flag the CRM
 * My Profile page shows, and it is the source of truth for "is Gmail connected".
 * A missing/expired token is a token problem, not a disconnection — callers should
 * never render "not connected" while `profileConnected` is true.
 *
 * Tokens never reach the browser: the `fetchGmailMessages` callable reads the
 * stored grant server-side, refreshes it when needed, and returns parsed messages.
 *
 * @param {string} userId - The user ID
 * @param {string} query - Gmail search query (e.g., "from:email@example.com")
 * @param {number} maxResults - Maximum number of results to return (default: 50)
 * @param {{format?: 'metadata'}} [options] - 'metadata' returns headers only (no bodies), up to 500
 * @returns {Promise<{messages: Array, status: 'ok'|'not_connected'|'needs_reauth'|'error', profileConnected: boolean, error?: string}>}
 */
export const fetchGmailMessagesWithStatus = async (userId, query, maxResults = 50, options = {}) => {
  try {
    const callable = httpsCallable(functions, 'fetchGmailMessages');
    const { data } = await callable({ userId, query, maxResults, ...(options.format ? { format: options.format } : {}) });
    const result = data || {};

    // The server sends dates as ISO strings; callers sort and format Date objects.
    const messages = (Array.isArray(result.messages) ? result.messages : [])
      .map((msg) => ({ ...msg, date: msg?.date ? new Date(msg.date) : new Date(0) }))
      .sort((a, b) => b.date - a.date);

    return {
      messages,
      status: result.status || 'error',
      profileConnected: result.profileConnected === true,
      ...(result.error ? { error: result.error } : {})
    };
  } catch (error) {
    console.error('Error fetching Gmail messages:', error);

    // Best known connection state: the profile's non-secret connected flag.
    let profileConnected = false;
    try {
      const integrationsStatus = await getUserIntegrationsStatus(userId);
      profileConnected = integrationsStatus?.gmail?.connected === true;
    } catch {
      /* keep false */
    }

    return { messages: [], status: 'error', error: error.message, profileConnected };
  }
};

/**
 * Download one attachment from a staff member's mailbox as a Blob. The message
 * listing returns attachment metadata only; bytes come from the
 * `fetchGmailAttachment` callable, keyed by the attachment's part id.
 *
 * @param {string} userId - The staff member whose inbox holds the message (message.fetchedBy.id)
 * @param {string} messageId - That mailbox's Gmail message id
 * @param {string} partId - attachment.partId from the message listing
 * @returns {Promise<{blob: Blob, filename: string, mimeType: string}>} throws with a readable message
 */
export const fetchGmailAttachment = async (userId, messageId, partId) => {
  const callable = httpsCallable(functions, 'fetchGmailAttachment');
  const { data } = await callable({ userId, messageId, partId });
  const status = data?.status;
  if (status !== 'ok' || !data?.attachment?.data) {
    const reasons = {
      too_large: 'This attachment is too large to open here. Open it in Gmail instead.',
      not_found: 'This attachment is no longer in the mailbox.',
      not_connected: 'The Gmail inbox this email came from is no longer connected.',
      needs_reauth: 'The Gmail inbox this email came from needs to be reconnected.',
    };
    throw new Error(reasons[status] || data?.error || 'Could not load this attachment.');
  }

  const { filename, mimeType, data: base64 } = data.attachment;
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return { blob: new Blob([bytes], { type: mimeType }), filename, mimeType };
};

/**
 * Fetch Gmail messages for a user (messages only)
 * @param {string} userId - The user ID
 * @param {string} query - Gmail search query (e.g., "from:email@example.com")
 * @param {number} maxResults - Maximum number of results to return (default: 50)
 * @returns {Promise<Array>} Array of Gmail messages
 */
export const fetchGmailMessages = async (userId, query, maxResults = 50) => {
  const { messages } = await fetchGmailMessagesWithStatus(userId, query, maxResults);
  return messages;
};

/**
 * Fetch Gmail messages for a specific email address (from or to)
 * @param {string} userId - The user ID
 * @param {string} emailAddress - Email address to search for
 * @param {number} maxResults - Maximum number of results to return
 * @returns {Promise<Array>} Array of Gmail messages
 */
export const fetchGmailMessagesForEmail = async (userId, emailAddress, maxResults = 50) => {
  const { messages } = await fetchGmailMessagesForEmailWithStatus(userId, emailAddress, maxResults);
  return messages;
};

/**
 * Fetch Gmail messages involving an email address, along with the connection status
 * @param {string} userId - The user ID
 * @param {string} emailAddress - Email address to search for
 * @param {number} maxResults - Maximum number of results to return
 * @returns {Promise<{messages: Array, status: 'ok'|'not_connected'|'needs_reauth'|'error', profileConnected: boolean, error?: string}>}
 */
export const fetchGmailMessagesForEmailWithStatus = async (userId, emailAddress, maxResults = 50) => {
  // One address, or several for one contact (primary + their other emails)
  const addresses = (Array.isArray(emailAddress) ? emailAddress : [emailAddress]).filter(Boolean);
  if (!addresses.length) {
    return { messages: [], status: 'ok', profileConnected: true };
  }

  // Search for emails from or to these addresses
  const query = addresses.map((a) => `from:${a} OR to:${a}`).join(' OR ');
  return await fetchGmailMessagesWithStatus(userId, query, maxResults);
};
